import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PiFastForwardCircle } from "react-icons/pi";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Filler,
} from "chart.js";
import "chartjs-adapter-date-fns";
import NavBar from "../../components/common/NavBar";
import { useProductScope, scopeTitle, scopeName, scopeFilePrefix } from "../../contexts/ProductScope";
import { openDataAvailable } from "../../config/openData";
import PopulationBaseline from "../../components/emergency/PopulationBaseline";
import BaselineCellLayer from "../../components/emergency/BaselineCellLayer";
import BaselineLayerControl from "../../components/emergency/BaselineLayerControl";
import { useBaselineLayer } from "../../hooks/emergency/useBaselineLayer";
import Spinner from "../../components/common/Spinner";
import {
  realtimeApi,
  RealtimeRow,
  RealtimeBucket,
} from "../../services/realtimeApi";
import { usePollingRefresh } from "../../hooks/realtime/usePollingRefresh";
import { useNightFloor, localNightFloorStore, MIN_QUIET_SAMPLES } from "../../hooks/realtime/useNightFloor";
import { readZone } from "../../realtime/zoneReadings";
import { useRealtimeCountry } from "../../hooks/realtime/useRealtimeCountry";
import { useRoamingPulse } from "../../hooks/realtime/useRoamingPulse";
import { userMapsApi } from "../../services/userMapsApi";
import RealtimeAreaMap from "../../components/common/RealtimeAreaMap";
import { TrendInline } from "../../components/realtime/TrendBits";
import { realtimeDataIsHere, realtimeEnvLabel } from "../../utils/realtimeEnv";
import { DateRange, DEFAULT_RANGE } from "../../components/common/DateRangePicker";
import CountryView from "../../components/realtime/CountryView";
import RoamingPulse from "../../components/realtime/RoamingPulse";
import RealtimeViewSwitch, { RealtimeView } from "../../components/realtime/RealtimeViewSwitch";
import NightFloorControl from "../../components/realtime/NightFloorControl";
import { dailyCountryRows, countrySplitFromDaily, rangeLabelFor } from "../../utils/realtimeCountry";
import { downloadCsv, csvFilename } from "../../utils/csv";
import { theme, withAlpha, chartColors } from "../../config/theme";
import { useTheme } from "../../contexts/ThemeContext";

ChartJS.register(LineElement, PointElement, LinearScale, TimeScale, Tooltip, Filler);

const FastForwardIcon = PiFastForwardCircle as any;

// Table columns depend on granularity: aggregated rows have no per-sample
// `result`, but carry the sample count and the bucket's own peak.
const COLUMNS_RAW = ["Time", "Target", "Result", "Subscribers"];
const COLUMNS_BUCKETED = ["Time", "Target", "Samples", "Subscribers (avg)", "Peak"];
const ts = (r: RealtimeRow) => +new Date(r.timestamp);

function timeAgo(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 90) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 36 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

const RealtimeDashboard: React.FC = () => {
  const productScope = useProductScope();
  const isEmergency = productScope.product === "emergency";
  // The baseline tab and layer need the open data API (or explicit draft mocks); without it they are hidden.
  const baselineAvailable = isEmergency && openDataAvailable();
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get("id");
  const targetName = searchParams.get("name");

  const [rows, setRows] = useState<RealtimeRow[]>([]);
  // Granularity the backend chose for the current range: it aggregates wide
  // ranges rather than truncating them, so the rows may be hour/day means.
  const [bucket, setBucket] = useState<RealtimeBucket>("raw");
  const bucketed = bucket !== "raw";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isDark } = useTheme();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [wkt, setWkt] = useState<string | null>(null);
  // Emergency only: the optional 250 m cell layer on the map. Stays off (and idle) unless switched on.
  const baselineLayer = useBaselineLayer(baselineAvailable && wkt ? [wkt] : []);
  const [areaBqProject, setAreaBqProject] = useState<string | null | undefined>(undefined);
  const [view, setView] = useState<RealtimeView>("timeline");
  // Night-floor baseline: ambient background deducted from the series. Its
  // SOURCE window (which nights the floor is measured from) is user-selected
  // and independent of the visible date range. Computed server-side by
  // GET /realtime/baseline; fetch, persistence and the missing-endpoint case
  // all live in the hook.
  const nightFloorStore = useMemo(
    () => localNightFloorStore(targetId ? `telcofy.nightfloor.${targetId}` : null),
    [targetId]
  );
  const nightFloor = useNightFloor({ store: nightFloorStore, targetId: targetId ?? undefined });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await realtimeApi.getRealtimeData({
          targetId: targetId ?? undefined,
          datetimeFrom: dateRange.from ?? undefined,
          datetimeTo: dateRange.to ?? undefined,
        });
        if (cancelled) return;
        setRows(data.rows);
        setBucket(data.bucket);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : `Failed to load ${scopeName(productScope, "realtime")} data`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetId, dateRange, productScope]);

  // Auto-refresh while the range is open-ended ("up to now"): silently re-fetch
  // so the dashboard stays live (backend samples ~every 10 min). No spinner —
  // rows swap in place. Paused when the range has a fixed end or the tab hides.
  const isLive = dateRange.to === null;
  usePollingRefresh(
    () => {
      realtimeApi
        .getRealtimeData({
          targetId: targetId ?? undefined,
          datetimeFrom: dateRange.from ?? undefined,
          datetimeTo: dateRange.to ?? undefined,
        })
        .then((d) => {
          setRows(d.rows);
          setBucket(d.bucket);
        })
        .catch(() => {});
    },
    60000,
    isLive
  );

  // Best-effort: fetch the polygon's geometry so we can show it on a map.
  useEffect(() => {
    if (!targetId && !targetName) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await userMapsApi.loadGeometries();
        if (cancelled) return;
        const area = res.maps.find(
          (m) => (!!targetId && m.id === targetId) || (!!targetName && m.name === targetName)
        );
        if (area) setAreaBqProject(area.bqProjectId ?? null);
        if (area?.geometry) setWkt(area.geometry);
      } catch {
        /* no map — non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetId, targetName]);

  // This view scopes the floor to its one target, so it reads the single entry.
  const baseline = nightFloor.baselines[0] ?? null;
  // Nights the floor may be measured from (dates with quiet-hour data).
  const nights = nightFloor.nights;

  // Shared country + roaming data, scoped to this one target. Same hooks and
  // aggregation as the project dashboard — only the scope differs.
  const scope = useMemo(() => (targetId ? new Set([targetId]) : null), [targetId]);
  const country = useRealtimeCountry({
    enabled: view === "country",
    targetId: targetId ?? undefined,
    dateRange,
  });
  const { days: countryDays, rows: countryRowsDaily } = useMemo(
    () => dailyCountryRows(country.rows, scope),
    [country.rows, scope]
  );
  const countrySplit = useMemo(() => countrySplitFromDaily(countryRowsDaily), [countryRowsDaily]);
  const rangeLabel = rangeLabelFor(dateRange);
  const roaming = useRoamingPulse({ enabled: view === "roaming", scope });
  const wktById = useMemo(
    () => (targetId && wkt ? new Map([[targetId, wkt]]) : new Map<string, string>()),
    [targetId, wkt]
  );

  // Rows for the selected target, oldest → newest.
  const series = useMemo(() => {
    let r = rows;
    if (targetId || targetName) {
      r = rows.filter(
        (x) =>
          (!!targetId && String(x.target_id) === targetId) ||
          (!!targetName && x.target_name === targetName)
      );
    }
    return [...r].sort((a, b) => ts(a) - ts(b));
  }, [rows, targetId, targetName]);

  // The zone's reading: KPIs + chart read its floor-adjusted rows; the raw
  // data table stays raw. Subtracting a constant leaves the 1h trend DELTA
  // unchanged (it cancels) but shrinks the % denominator — expected, not a bug.
  const reading = useMemo(
    () => readZone(series, { nightFloor: nightFloor.enabled ? baseline?.night_floor : null, bucketed }),
    [series, nightFloor.enabled, baseline, bucketed]
  );
  const adjustedSeries = useMemo(() => reading?.adjustedRows ?? [], [reading]);
  const stats = reading && {
    now: reading.now,
    latestTs: reading.latestAt,
    peak: reading.peakRow,
    peakValue: reading.peak,
    delta: reading.delta,
    pct: reading.pct,
    count: reading.sampleCount,
    successPct: reading.successPct,
  };

  const chartData = useMemo(() => {
    if (!adjustedSeries.length) return null;
    // The server already windowed the rows to the selected range; plot them all.
    const points = adjustedSeries.map((s) => ({ x: ts(s), y: s.subscriber_count }));
    return {
      datasets: [
        {
          label: "Subscribers present",
          data: points,
          borderColor: theme.brand500,
          backgroundColor: withAlpha(theme.brand500, 0.12),
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
      ],
    };
  }, [adjustedSeries]);

  const chartOptions = useMemo(
    () =>
      ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {

              label: (item: any) => ` ${item.parsed.y.toLocaleString()} present`,
            },
          },
        },
        scales: {
          x: {
            type: "time",
            time: {
              tooltipFormat: "EEE d MMM yyyy, HH:mm",
              displayFormats: { minute: "HH:mm", hour: "d MMM HH:mm", day: "d MMM", week: "d MMM", month: "MMM yyyy" },
            },
            ticks: { maxTicksLimit: 7, font: { size: 11 } },
            grid: { display: false },
          },
          y: {
            beginAtZero: false,
            ticks: {
              font: { size: 11 },
              callback: (v: any) => (typeof v === "number" ? v.toLocaleString() : v),
            },
            grid: { color: chartColors(isDark).grid },
          },
        },
        interaction: { mode: "index", intersect: false },
      } as any),
    [isDark]
  );

  const heading = targetName || targetId || "All targets";
  const otherEnv = !realtimeDataIsHere(areaBqProject);

  const nightFloorStatus = !baseline
    ? null
    : baseline.night_floor != null
    ? `${nightFloor.enabled ? "Deducting" : "Night floor"} ≈ ${baseline.night_floor.toLocaleString()} (median of 02–05h across ${baseline.nights_used} night${baseline.nights_used === 1 ? "" : "s"}, ${baseline.quiet_samples} samples)${nightFloor.enabled ? "." : " — switch to Night floor to deduct it."}`
    : `Not enough night-time samples in the selected nights (need ≥ ${MIN_QUIET_SAMPLES}) — showing raw counts.`;

  // Download the plotted series (baseline-adjusted when the night floor is on).
  const exportCsv = () => {
    // Aggregated exports carry the spread + sample count, and name the value
    // column so nobody mistakes a bucket mean for a sample.
    const rows: (string | number)[][] = [
      bucketed
        ? ["timestamp", "subscribers_avg", "subscribers_min", "subscribers_max", "samples"]
        : ["timestamp", "subscribers"],
    ];
    adjustedSeries.forEach((r) =>
      rows.push(
        bucketed
          ? [
              r.timestamp,
              r.subscriber_count,
              r.subscriber_count_min ?? "",
              r.subscriber_count_max ?? "",
              r.sample_count ?? "",
            ]
          : [r.timestamp, r.subscriber_count]
      )
    );
    downloadCsv(csvFilename(scopeFilePrefix(productScope, "realtime"), heading), rows);
  };

  const tableRows = useMemo(() => [...series].reverse().slice(0, 500), [series]);

  return (
    <div className="min-h-screen bg-paper-200">
      <NavBar title={scopeTitle(productScope, "Realtime")} showBackButton={true} />

      <div className="mx-auto max-w-none px-4 py-10 sm:px-6 lg:px-8 2xl:px-10">
        <div className="mb-8 flex items-center gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
            <FastForwardIcon className="h-6 w-6" />
          </div>
          <div>
            <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ink-faint">
              {scopeTitle(productScope, "Realtime")}
              {isLive && stats && (
                <span className="inline-flex items-center gap-1 rounded-full bg-ok-soft px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-ok-ink">
                  <span className="h-1.5 w-1.5 rounded-full bg-ok animate-pulse" />
                  Live
                </span>
              )}
              {isLive && !stats && (
                <span className="inline-flex items-center gap-1 rounded-full bg-warn-soft px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-warn-ink">
                  <span className="h-1.5 w-1.5 rounded-full bg-warn" />
                  No live data
                </span>
              )}
            </p>
            <h1 className="font-display text-3xl font-bold tracking-tight text-ink">
              {heading}
            </h1>
            {targetId && <p className="text-sm text-ink-muted">Target ID {targetId}</p>}
          </div>
        </div>

        {otherEnv && (
          <div className="mb-6 rounded-2xl border border-warn-line bg-warn-soft px-5 py-4 text-sm text-warn-ink">
            This area's {scopeName(productScope, "realtime")} data lives in the{" "}
            <span className="font-semibold">{realtimeEnvLabel(areaBqProject)}</span>{" "}
            environment, so it can't be shown here. Switch environments to view it.
          </div>
        )}

        <RealtimeViewSwitch
          view={view}
          onView={setView}
          dateRange={dateRange}
          onDateRange={setDateRange}
          rangeLabel={rangeLabel}
          roamingInterval={roaming.interval}
          onRoamingInterval={roaming.setInterval}
          showBaseline={baselineAvailable}
        />

        {view === "timeline" && targetId && !otherEnv && !nightFloor.unavailable && (
          <NightFloorControl
            loading={nightFloor.loading}
            nights={nights}
            enabled={nightFloor.enabled}
            onToggle={nightFloor.setEnabled}
            fromNight={nightFloor.range?.from ?? null}
            toNight={nightFloor.range?.to ?? null}
            onRange={nightFloor.setRange}
            status={nightFloorStatus}
          />
        )}

        {view === "baseline" && baselineAvailable ? (
          <PopulationBaseline geometries={wkt ? [wkt] : []} areaLabel={heading} />
        ) : view === "roaming" ? (
          <RoamingPulse
            loading={roaming.loading}
            title={heading}
            notEnabled={roaming.notEnabled}
            error={roaming.error}
            zones={roaming.zones}
            updatedAt={roaming.updatedAt}
            intervalLabel={`${roaming.interval} min`}
            wktById={wktById}
          />
        ) : view === "country" ? (
          <CountryView
            loading={country.loading}
            notEnabled={country.notEnabled}
            error={country.error}
            days={countryDays}
            rows={countryRowsDaily}
            split={countrySplit}
            rangeLabel={rangeLabel}
          />
        ) : loading && !rows.length ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-bad-line bg-bad-soft p-6 text-bad-ink">
            {error}
          </div>
        ) : !stats ? (
          <div className="mb-6 rounded-2xl border border-paper-300/70 bg-surface p-10 text-center text-ink-muted">
            No realtime data for this target yet.
          </div>
        ) : (
          <>
            {/* KPI tiles */}
            <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
              <div className="rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
                <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                  Present now
                </p>
                <p className="mt-1 font-display text-3xl font-bold text-ink">
                  {stats.now.toLocaleString()}
                </p>
                <p className="text-xs text-ink-muted">
                  updated {timeAgo(Date.now() - stats.latestTs)}
                </p>
              </div>

              <div className="rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
                <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                  Peak
                </p>
                <p className="mt-1 font-display text-3xl font-bold text-ink">
                  {stats.peakValue.toLocaleString()}
                </p>
                <p className="text-xs text-ink-muted">
                  {/* A day bucket's start instant is meaningless as a clock
                      time (it renders as the local equivalent of UTC midnight),
                      so show the date for daily granularity. */}
                  at{" "}
                  {bucket === "day"
                    ? new Date(stats.peak.timestamp).toLocaleDateString()
                    : new Date(stats.peak.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                </p>
              </div>

              <div className="rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
                <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                  Trend (1h)
                </p>
                <p
                  className={`mt-1 font-display text-3xl font-bold ${
                    stats.delta > 0
                      ? "text-ok-ink"
                      : stats.delta < 0
                      ? "text-bad-ink"
                      : "text-ink"
                  }`}
                >
                  {stats.delta > 0 ? "▲" : stats.delta < 0 ? "▼" : "—"}{" "}
                  {Math.abs(stats.pct).toFixed(0)}%
                </p>
                <p className="text-xs text-ink-muted">
                  {stats.delta >= 0 ? "+" : ""}
                  {stats.delta.toLocaleString()} vs 1h ago
                </p>
              </div>

              <div className="rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
                <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                  Samples
                </p>
                <p className="mt-1 font-display text-3xl font-bold text-ink">
                  {stats.count.toLocaleString()}
                </p>
                <p className="text-xs text-ink-muted">
                  {stats.successPct != null
                    ? `${stats.successPct}% success`
                    : `in ${bucket === "day" ? "daily" : "hourly"} buckets`}
                </p>
              </div>
            </div>

            {/* Time series */}
            <div className="mb-6 rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-lg font-semibold text-ink">
                  Subscribers present
                  {bucketed && (
                    <span className="ml-2 rounded bg-paper-200 px-1.5 py-0.5 align-middle text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                      {bucket === "day" ? "daily" : "hourly"} average
                    </span>
                  )}
                  {nightFloor.enabled && baseline?.night_floor != null && (
                    <span className="ml-2 rounded bg-brand-50 px-1.5 py-0.5 align-middle text-[10px] font-mono uppercase tracking-wider text-brand-700">
                      − {baseline.night_floor.toLocaleString()} baseline
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportCsv}
                    disabled={!adjustedSeries.length}
                    className="rounded-lg border border-paper-300 px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-paper-100 disabled:opacity-40"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
              <div className="h-72">
                {chartData && <Line data={chartData} options={chartOptions} />}
              </div>
            </div>

            {/* Location */}
            {wkt && (
              <div className="mb-6 overflow-hidden rounded-2xl border border-paper-300/70 bg-surface shadow-soft">
                <div className="border-b border-paper-200 px-4 py-3">
                  <h2 className="font-display text-lg font-semibold text-ink">Location</h2>
                </div>
                <div className="h-80 lg:h-[28rem] 2xl:h-[36rem]">
                  <RealtimeAreaMap
                    polygons={[
                      {
                        id: targetId ?? "area",
                        name: heading,
                        wkt,
                        label: stats ? (
                          <span className="flex items-center gap-1.5">
                            <span className="font-medium text-ink">{stats.now.toLocaleString()}</span>
                            <TrendInline v={stats.delta} />
                          </span>
                        ) : undefined,
                      },
                    ]}
                    {...(baselineAvailable
                      ? {
                          overlayBottomLeft: <BaselineLayerControl layer={baselineLayer} />,
                          underlay: baselineLayer.on && baselineLayer.data ? <BaselineCellLayer cells={baselineLayer.data.cells} /> : null,
                        }
                      : {})}
                  />
                </div>
              </div>
            )}

            {/* Raw data */}
            <div className="overflow-hidden rounded-2xl border border-paper-300/70 bg-surface">
              <div className="flex items-center justify-between border-b border-paper-200 px-4 py-3">
                <span className="text-sm font-medium text-ink">
                  {bucketed ? (bucket === "day" ? "Daily data" : "Hourly data") : "Raw data"}
                </span>
                <span className="text-xs text-ink-faint">newest first</span>
              </div>
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-paper-100">
                    <tr>
                      {(bucketed ? COLUMNS_BUCKETED : COLUMNS_RAW).map((h) => (
                        <th
                          key={h}
                          className="px-4 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-ink-faint"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((r, i) => (
                      <tr key={i} className="border-t border-paper-200">
                        <td className="whitespace-nowrap px-4 py-2 text-ink-muted">
                          {bucket === "day"
                            ? new Date(r.timestamp).toLocaleDateString()
                            : new Date(r.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-ink">{r.target_name}</td>
                        {bucketed ? (
                          <td className="px-4 py-2 tabular-nums text-ink-muted">
                            {(r.sample_count ?? 0).toLocaleString()}
                          </td>
                        ) : (
                          <td className="px-4 py-2 text-ink-muted">{r.result}</td>
                        )}
                        <td className="px-4 py-2 tabular-nums text-ink">
                          {r.subscriber_count.toLocaleString()}
                        </td>
                        {bucketed && (
                          <td className="px-4 py-2 tabular-nums text-ink-muted">
                            {(r.subscriber_count_max ?? r.subscriber_count).toLocaleString()}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RealtimeDashboard;
